import { useReactiveVar } from '@apollo/client';
import { Button } from '@synoptic/ui-kit/button/button.js';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from '@synoptic/ui-kit/dropdown-menu/dropdown-menu.js';
import { CopyIcon } from '@synoptic/ui-kit/icons/react/copy.js';
import { DownloadIcon } from '@synoptic/ui-kit/icons/react/download.js';
import { LinkIcon } from '@synoptic/ui-kit/icons/react/link.js';
import { MediaImageIcon } from '@synoptic/ui-kit/icons/react/media-image.js';
import { ShareIcon } from '@synoptic/ui-kit/icons/react/share.js';
import { XTwitterIcon } from '@synoptic/ui-kit/icons/react/x-twitter.js';
import { useThemeMode } from '@synoptic/ui-kit/theme/theme-context.js';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { useQueryClient } from '@tanstack/react-query';
import { queryOptions } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { href, useSearchParams } from 'react-router';
import {
  getPageScreenshotUrl,
  getPageScreenshotVersion,
} from '@/routes-utils/page-screenshot-url';
import { NodeDataMini } from '@/services/twttr-graph-service';
import { copyUrlToClipboard } from '@/utils/copy-url-to-clipboard';
import { traceError } from '@/utils/trace-error';
import { sigmaInstanceVar } from './sigma-options-var';
import { shareActions } from '../route.css';

export const SigmaShareButton = ({
  selectedNodeData,
}: {
  selectedNodeData?: NodeDataMini | null;
}) => {
  const toast = useToast();
  const [searchParams] = useSearchParams();

  const [downloading, setDownloading] = useState(false);
  const actionSelectedRef = useRef(false);

  const themeMode = useThemeMode();
  const sigma = useReactiveVar(sigmaInstanceVar);
  const queryClient = useQueryClient();

  const getPreviewUrl = () => {
    if (!sigma) {
      throw new Error('Sigma not initialized');
    }

    const dimensions = sigma.getDimensions();
    const cameraState = sigma.getCamera().getState();

    const previewSearchParams = new URLSearchParams(searchParams);

    previewSearchParams.set('cs', JSON.stringify(cameraState));
    // TODO: better implement it on page-screenshot lambda with `await page.emulateMediaFeatures([{name: 'prefers-color-scheme', value: 'dark' }]);`
    previewSearchParams.set('theme', themeMode);

    return getPageScreenshotUrl({
      type: 'ct',
      id: 'graph',
      version: getPageScreenshotVersion(previewSearchParams.toString()),
      w: dimensions.width,
      h: dimensions.height,
      routeSearchParams: previewSearchParams,
    });
  };

  const getShareXText = () => {
    return selectedNodeData
      ? `See where @${selectedNodeData.screen_name} stands in the Crypto Twitter Network by @SynopticCom\n`
      : 'Crypto Twitter Network Visualization by @SynopticCom\n';
  };

  const trackShareAction = (
    action: 'Copy Image' | 'Download Image' | 'Share on X' | 'Copy Link',
  ) => {
    window.mixpanel?.track('CT Graph Share Action', {
      selectedNodeData: selectedNodeData?.screen_name,
      action,
    });
  };

  const getImageQueryOptions = () => {
    const url = getPreviewUrl();
    return queryOptions({
      queryKey: ['sigma-graph-image', url],
      queryFn: async ({ signal }) => {
        const response = await fetch(url, { signal });
        if (!response.ok) {
          traceError(
            new Error(
              `Failed to load image: [${response.status}] ${response.statusText}`,
            ),
          );
          throw new Error('Please try again later');
        }
        return response.blob();
      },
      staleTime: Infinity,
      retry: false,
    });
  };

  const loadImageBlob = async () => {
    const queryOptions = getImageQueryOptions();
    const data = await queryClient.fetchQuery(queryOptions);
    return data;
  };

  const preloadImage = () => {
    actionSelectedRef.current = false;
    const queryOptions = getImageQueryOptions();

    queryClient.prefetchQuery(queryOptions).catch(() => {});
  };

  const copyImage = async () => {
    try {
      actionSelectedRef.current = true;
      setDownloading(true);
      const blob = await loadImageBlob();

      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob }),
      ]);

      trackShareAction('Copy Image');

      toast.success({ title: 'Image copied to clipboard' });
    } catch (error) {
      toast.error({
        title: 'Failed to copy image',
        subTitle: (error as Error)?.message,
      });
    } finally {
      actionSelectedRef.current = false;
      setDownloading(false);
    }
  };

  const downloadImage = async () => {
    let objectUrl;
    try {
      actionSelectedRef.current = true;
      setDownloading(true);
      const blob = await loadImageBlob();

      trackShareAction('Download Image');

      objectUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `synoptic-ct-graph.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error({
        title: 'Failed to export image',
        subTitle: (error as Error)?.message,
      });
    } finally {
      actionSelectedRef.current = false;
      setDownloading(false);
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    }
  };

  return (
    <div className={shareActions}>
      <DropdownMenuRoot>
        <DropdownMenuTrigger>
          <Button
            fullWidth
            startIcon={<ShareIcon />}
            size="small"
            variant="outline"
          >
            Share
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent>
          <DropdownMenuItem
            icon={<LinkIcon />}
            onSelect={() => {
              trackShareAction('Copy Link');
              copyUrlToClipboard(href('/ct') + window.location.search);
              toast.info({ title: 'Link copied to clipboard' });
            }}
          >
            Copy Link
          </DropdownMenuItem>
          <a
            style={{ all: 'unset' }}
            target="_blank"
            href={`https://x.com/intent/tweet?text=${encodeURIComponent(getShareXText())}&url=${encodeURIComponent('https://synoptic.com' + href('/ct') + location.search)}`}
            rel="noreferrer"
          >
            <DropdownMenuItem
              icon={<XTwitterIcon />}
              onSelect={() => trackShareAction('Share on X')}
            >
              Share on X
            </DropdownMenuItem>
          </a>
        </DropdownMenuContent>
      </DropdownMenuRoot>

      <DropdownMenuRoot
        onOpenChange={(open) => {
          if (open) {
            preloadImage();
          } else {
            if (!actionSelectedRef.current) {
              queryClient.cancelQueries({ queryKey: ['sigma-graph-image'] });
            }
            actionSelectedRef.current = false;
          }
        }}
      >
        <DropdownMenuTrigger>
          <Button
            loading={downloading}
            fullWidth
            startIcon={<MediaImageIcon />}
            size="small"
            variant="outline"
          >
            Export Image
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent>
          <DropdownMenuItem icon={<CopyIcon />} onSelect={copyImage}>
            Copy Image
          </DropdownMenuItem>

          <DropdownMenuItem icon={<DownloadIcon />} onSelect={downloadImage}>
            Save Image As
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuRoot>
    </div>
  );
};
